import { cn } from '@/lib/cn';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  ensureHelpTargetVisible,
  placeVoiceCallout,
  resolveHelpTargetElement,
  voiceAssistantMessages,
  type CalloutPlacement,
  type Rect,
} from '@heidi/agents';
import { reportHighlightMiss } from './reportHighlightMiss';
import { useVoiceGuideStore } from './useVoiceGuideStore';
import { useVoiceHighlightStore } from './useVoiceHighlightStore';
import { VoiceHighlightCallout } from './VoiceHighlightCallout';

const pad = 6;

/**
 * Renders a dimmed spotlight around a curated help target while the voice
 * assistant is explaining UI. Callout is coachmark-anchored to the target so
 * it stays visible (not buried under the Talk to Heidi dock).
 */
export const VoiceHighlightOverlay = () => {
  const intl = useIntl();
  const highlight = useVoiceHighlightStore((s) => s.highlight);
  const clearHighlight = useVoiceHighlightStore((s) => s.clearHighlight);
  const guideActive = useVoiceGuideStore((s) => s.guide != null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [missing, setMissing] = useState(false);
  const [missingCopy, setMissingCopy] = useState<string | null>(null);
  const [calloutPlacement, setCalloutPlacement] = useState<CalloutPlacement | null>(null);
  const calloutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!highlight) {
      setRect(null);
      setMissing(false);
      setMissingCopy(null);
      return;
    }

    if (!highlight.target) {
      setRect(null);
      setMissing(false);
      setMissingCopy(null);
      return;
    }

    let cancelled = false;
    let tries = 0;
    let didInitialScroll = false;
    let didEnsureParents = false;
    let raf = 0;
    let retryTimer = 0;
    const requestedTarget = highlight.target;

    const measure = ({ allowScroll }: { allowScroll: boolean }) => {
      if (cancelled) return;
      const el = resolveHelpTargetElement(requestedTarget);
      if (!el) {
        if (!didEnsureParents) {
          didEnsureParents = true;
          void ensureHelpTargetVisible(requestedTarget).then(() => {
            if (cancelled) return;
            measure({ allowScroll });
          });
          return;
        }
        tries += 1;
        if (tries < 12) {
          retryTimer = window.setTimeout(() => measure({ allowScroll }), 120);
          return;
        }
        const report = reportHighlightMiss(requestedTarget);
        setMissing(true);
        // Keep clinician callout on the intl fallback — probe.explanation is
        // for the agent (raw target ids), not the callout body.
        setMissingCopy(null);
        setRect(null);
        // Fallback highlight (parent) is applied by reportHighlightMiss; remeasure.
        if (report.highlightedFallback) {
          retryTimer = window.setTimeout(() => {
            if (cancelled) return;
            const fallbackEl = resolveHelpTargetElement(report.highlightedFallback!);
            if (!fallbackEl) return;
            setMissing(false);
            setMissingCopy(null);
            if (allowScroll) {
              fallbackEl.scrollIntoView({
                block: 'nearest',
                inline: 'nearest',
                behavior: 'smooth',
              });
            }
            const box = fallbackEl.getBoundingClientRect();
            setRect({
              top: box.top - pad,
              left: box.left - pad,
              width: box.width + pad * 2,
              height: box.height + pad * 2,
            });
          }, 0);
        }
        return;
      }
      setMissing(false);
      setMissingCopy(null);
      if (allowScroll && !didInitialScroll) {
        didInitialScroll = true;
        el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
      const box = el.getBoundingClientRect();
      setRect({
        top: box.top - pad,
        left: box.left - pad,
        width: box.width + pad * 2,
        height: box.height + pad * 2,
      });
    };

    measure({ allowScroll: true });
    const scheduleRemeasure = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        measure({ allowScroll: false });
      });
    };
    window.addEventListener('resize', scheduleRemeasure);
    window.addEventListener('scroll', scheduleRemeasure, true);

    // Guides own their lifecycle; don't auto-dismiss the spotlight mid-step.
    const timeout = guideActive
      ? undefined
      : window.setTimeout(() => {
          clearHighlight();
        }, highlight.durationMs);

    return () => {
      cancelled = true;
      if (raf) window.cancelAnimationFrame(raf);
      if (retryTimer) window.clearTimeout(retryTimer);
      if (timeout !== undefined) window.clearTimeout(timeout);
      window.removeEventListener('resize', scheduleRemeasure);
      window.removeEventListener('scroll', scheduleRemeasure, true);
    };
  }, [highlight, clearHighlight, guideActive]);

  const showCallout = Boolean(
    highlight &&
    (highlight.title ||
      highlight.body ||
      missing ||
      missingCopy ||
      highlight.showContinue)
  );

  useLayoutEffect(() => {
    if (!showCallout) {
      setCalloutPlacement(null);
      return;
    }

    const update = () => {
      const el = calloutRef.current;
      const size = el
        ? { width: el.offsetWidth, height: el.offsetHeight }
        : { width: 288, height: 88 };
      setCalloutPlacement(
        placeVoiceCallout(rect, size, {
          width: window.innerWidth,
          height: window.innerHeight,
        })
      );
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [showCallout, rect, highlight?.title, highlight?.body, missing, missingCopy]);

  if (!highlight) return null;

  const calloutBody =
    missingCopy ??
    (missing
      ? intl.formatMessage(voiceAssistantMessages.highlightMissing)
      : highlight.body);

  return (
    <div
      // Above page chrome; below Talk dock (z-40) and Dialog (z-50) so the
      // clinician can still use the pill while a target is lit.
      className="pointer-events-none fixed inset-0 z-30"
      data-testid="voice-highlight-overlay"
      aria-live="polite"
    >
      {rect ? (
        <>
          <div
            className={cn(
              'absolute rounded-xl',
              // Semantic overlay token (`--bg-overlay`); never invent
              // `--twc-overlay` — that var does not exist and made the
              // cutout invisible (mic / any target looked "unhighlighted").
              'shadow-[0_0_0_9999px_var(--bg-overlay)]',
              'transition-all duration-300 ease-out-quart'
            )}
            data-testid="voice-highlight-spotlight"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            }}
          />
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute rounded-xl',
              'ring-2 ring-border-focus ring-offset-2 ring-offset-surface',
              'motion-safe:animate-pulse motion-reduce:animate-none'
            )}
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              animationDuration: '2.4s',
            }}
          />
        </>
      ) : null}
      {showCallout ? (
        <VoiceHighlightCallout
          calloutRef={calloutRef}
          placement={calloutPlacement}
          title={highlight.title}
          body={calloutBody}
          showContinue={highlight.showContinue}
        />
      ) : null}
    </div>
  );
};
